import ErrorIcon from '@mui/icons-material/Error';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import type { TextFieldProps } from '@mui/material/TextField';
import { useController } from 'react-hook-form';

type HookFormTextFieldProps = {
  name: string;
  handleOnBlur?: (value: string) => void;
} & TextFieldProps;

function HookFormTextField({
  name,
  label,
  placeholder = '',
  rows = 1,
  required = false,
  multiline = false,
  handleOnBlur
}: HookFormTextFieldProps) {
  const {
    field: { onChange, onBlur: formOnBlur, value, ref },
    formState: { errors }
  } = useController({
    name,
    rules: {
      required
    }
  });

  let additionalProps = {};

  if (multiline) {
    additionalProps = {
      rows,
      multiline: true
    };
  }

  const onBlur = () => {
    if (handleOnBlur) {
      handleOnBlur(value);
    }
    // call the onBlur func from react-hook-form
    formOnBlur();
  };

  return (
    <TextField
      fullWidth
      required={required}
      placeholder={placeholder}
      label={label}
      onChange={onChange}
      onBlur={onBlur}
      name={name}
      value={value}
      inputRef={ref}
      error={!!errors?.[name]?.message}
      helperText={(errors?.[name]?.message ?? null) as React.ReactNode}
      InputLabelProps={{
        sx: theme => ({
          marginTop: '-1px',
          '&.Mui-focused': {
            marginTop: 0
          },
          '&.MuiInputLabel-shrink': {
            backgroundColor: theme.palette.tofu.light,
            fontSize: '18px',
            paddingRight: '5px'
          },
          variant: 'outlined'
        })
      }}
      InputProps={{
        endAdornment:
          // istanbul ignore next
          !!errors?.[name]?.message ? (
            <InputAdornment position="end">
              <ErrorIcon color="error" />
            </InputAdornment>
          ) : null
      }}
      {...additionalProps}
    />
  );
}

export default HookFormTextField;
